/*
 * bc-cart-add
 *
 * Shared cart-add plumbing for the bc widgets that POST to /cart/add.js
 * themselves (bc-custom-length, bc-accessories, bc-primitive-kit-builder),
 * plus the section-id gather that bc-cart-length's /cart/change.js path
 * shares. Before this existed the same POST + /cart.js refetch +
 * cart:update dispatch was hand-rolled in three scripts (and the section-id
 * gather in four).
 *
 * The legacy-shaped 'cart:update' dispatched here is a CONTRACT with
 * assets/bc-events-compat.js (BC_CART_SOURCES): the shim forwards it to
 * 'shopify:cart:lines-update' with a pre-resolved promise so the v4 cart
 * drawer / items / header bubble morph from data.sections. One place to
 * update if that contract ever changes.
 *
 * Exposes `window.bcCartAdd`:
 *   - collectSectionIds() → comma-joined section ids of every
 *     cart-items-component on the page (for Shopify's section rendering)
 *   - add(items) → Promise<{ok, response, cart}>. ok=false means Shopify
 *     rejected the add (response carries status/message/description);
 *     network failures REJECT — callers show their own error UI either way.
 *   - getCart() → Promise<cart JSON | null> (null on any failure).
 *   - change(payload) → Promise<{ok, response}> POST to /cart/change.js
 *     ({id: <line key>, quantity, properties, ...}) with the section ids
 *     attached; response is the full cart JSON on success.
 *   - samePropsIgnoring(a, b, ignore) → property-set equality minus the
 *     `ignore` keys — the "same configured build" test behind the
 *     fold-instead-of-merge add paths (bc-segment-builder,
 *     bc-custom-length): Shopify natively merges identical-property lines
 *     by summing RAW quantity, which for custom-length lines is
 *     length-units, so those callers find the matching line and change()
 *     it with recomputed pieces/quantity instead of re-adding.
 *   - dispatchCartUpdate(target, {source, cart, response, itemCount,
 *     productId, variantId}) → emits the legacy cart:update.
 *
 * Loaded (defer) by each consumer's Liquid before its own script. IIFE-
 * guarded so duplicate loads are no-ops.
 */
(() => {
  if (window.bcCartAdd) return;

  function collectSectionIds() {
    const ids = new Set();
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
      const sid = el.getAttribute('data-section-id');
      if (sid) ids.add(sid);
    });
    return Array.from(ids).join(',');
  }

  async function add(items) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items, sections: collectSectionIds() }),
    });
    const response = await res.json();
    if (!res.ok || response.status) return { ok: false, response };
    const cart = await fetch('/cart.js').then((r) => r.json()).catch(() => null);
    return { ok: true, response, cart };
  }

  async function getCart() {
    try {
      const res = await fetch('/cart.js');
      return res.ok ? await res.json() : null;
    } catch (_e) {
      return null;
    }
  }

  async function change(payload) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...payload, sections: collectSectionIds() }),
    });
    let response = {};
    try { response = await res.json(); } catch (_e) { /* keep {} */ }
    return { ok: res.ok, response };
  }

  function samePropsIgnoring(a, b, ignore) {
    const skip = ignore || [];
    const ka = Object.keys(a || {}).filter((k) => !skip.includes(k));
    const kb = Object.keys(b || {}).filter((k) => !skip.includes(k));
    return ka.length === kb.length
      && ka.every((k) => (b || {})[k] != null && String(a[k]) === String(b[k]));
  }

  function dispatchCartUpdate(target, { source, cart, response, itemCount, productId, variantId }) {
    (target || document).dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart || response,
          sourceId: source,
          data: {
            source,
            itemCount,
            productId,
            variantId,
            sections: response && response.sections,
          },
        },
      })
    );
  }

  window.bcCartAdd = { collectSectionIds, add, getCart, change, samePropsIgnoring, dispatchCartUpdate };
})();
